import { useEffect, useMemo, useState } from "react";
import { Calculator, CheckCircle2, AlertTriangle, RotateCcw, Info, TrendingUp, Wallet, Percent, Target } from "lucide-react";
import { fmtNumber } from "@/lib/api";
import { loadSessionState, scheduleSessionWrite, clearSectionState } from "@/lib/sessionState";

const fmtUsd = (n) => {
  if (n === null || n === undefined || !isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
};

const num = (v) => {
  const n = Number(v);
  return isFinite(n) ? n : 0;
};

const RoiField = ({ label, hint, value, onChange, prefix, suffix, testid, step = 1, placeholder }) => (
  <label className="block">
    <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{label}</div>
    <div className="flex items-center border hairline rounded-md overflow-hidden" style={{ background: "var(--surface-2)" }}>
      {prefix && <span className="mono text-xs text-zinc-500 px-2">{prefix}</span>}
      <input
        type="number"
        data-testid={testid}
        value={value}
        min={0}
        step={step}
        placeholder={placeholder || ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") { onChange(""); return; }
          const n = Number(raw);
          if (!isNaN(n)) onChange(Math.max(0, n));
        }}
        className="flex-1 min-w-0 bg-transparent px-2 py-2 text-sm text-white outline-none mono placeholder:text-zinc-700"
      />
      {suffix && <span className="mono text-xs text-zinc-500 px-2">{suffix}</span>}
    </div>
    {hint && <div className="mono text-[10px] text-zinc-600 mt-1">{hint}</div>}
  </label>
);

// ---- Single-source-of-truth colour system --------------------------------
// Semantic rules (used across the whole calculator so nothing looks random):
//   REVENUE (money-IN metrics: Total Volume, Gross Revenue)  → primary / cyan
//   COST    (money-OUT metrics: Upfront, Commissions, Total Cost) → cost / amber
//   VERDICT (final outcome metrics: Net Profit, Expected ROI)     → good/bad by sign
//   ADVICE  (suggested changes inside Scenario B cards)            → advice / yellow
//   HEADROOM (safe-margin cards inside Scenario A)                 → good / green
// Muted background + tinted top-edge give each card a subtle family tint
// without visual noise.
const PALETTE = {
  primary: "#00F0FF",  // cyan — revenue-side
  cost:    "#FBBF24",  // amber — cost-side
  good:    "#39FF14",  // green — pass / headroom
  bad:     "#F43F5E",  // red — fail / loss
  advice:  "#EAB308",  // yellow — negotiation levers
  neutral: "#A1A1AA",  // zinc — no-signal fallback
};

const OutputBox = ({ label, value, tone, testid, sub }) => {
  const c = tone || PALETTE.neutral;
  return (
    <div className="border rounded-md p-4 transition-colors duration-150" data-testid={testid}
         style={{ background: "var(--surface-2)", borderColor: `${c}33`, boxShadow: `inset 0 1px 0 ${c}22` }}>
      <div className="mono text-[10px] uppercase tracking-[0.2em]" style={{ color: `${c}CC` }}>{label}</div>
      <div className="display text-xl font-semibold mt-1.5" style={{ color: c }}>{value}</div>
      {sub && <div className="mono text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
};

const AlternativeCard = ({ icon: Icon, title, value, blurb, tone, testid }) => {
  const c = tone || PALETTE.neutral;
  return (
    <div className="border rounded-md p-4 transition-colors duration-150" data-testid={testid}
         style={{ background: "var(--surface-2)", borderColor: `${c}33`, boxShadow: `inset 0 1px 0 ${c}22` }}>
      <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: `${c}CC` }}>
        <Icon size={12} style={{ color: c }} /> {title}
      </div>
      <div className="display text-lg font-semibold" style={{ color: c }}>{value}</div>
      <div className="text-xs text-zinc-300 leading-relaxed mt-2">{blurb}</div>
    </div>
  );
};

const EMPTY_INPUTS = {
  basis: "monthly",           // "monthly" | "total"
  monthlyVolume: "",
  totalVolume: "",
  tenureMonths: "",
  upfrontPerMonth: "",
  upfrontLumpSum: "",
  commissionPct: "",
  feesPct: "",
  requiredRoi: "",
};

const REQUIRED_KEYS_MONTHLY = ["monthlyVolume", "tenureMonths", "upfrontPerMonth", "commissionPct", "feesPct", "requiredRoi"];
const REQUIRED_KEYS_TOTAL = ["totalVolume", "upfrontLumpSum", "commissionPct", "feesPct", "requiredRoi"];

export const RoiCalculator = ({ subject, testidPrefix = "roi", framing = "onboard" }) => {
  // Persistence: inputs are remembered per calculator instance so an
  // in-progress model isn't lost when the user jumps to Compare / Search
  // and back. Keyed by testidPrefix so the page-level ROI Calculator and
  // any per-KOL embedded calculators don't clobber each other's numbers.
  const SECTION = `roi:${testidPrefix}`;
  const DEFAULTS = { inputs: { ...EMPTY_INPUTS }, mode: framing === "retention" ? "retention" : "acquisition" };
  const stored = useMemo(() => loadSessionState(SECTION, DEFAULTS), [SECTION]);
  const [inputs, setInputs] = useState(stored.inputs);
  // Mode swaps the copy shown alongside every scenario (acquisition = winning
  // a new creator; retention = keeping / renewing one). Math stays identical
  // so BDs can flip modes without re-entering any inputs. Seeded from the
  // caller's `framing` prop OR from the last remembered mode.
  const [mode, setMode] = useState(stored.mode);
  useEffect(() => {
    scheduleSessionWrite(SECTION, { inputs, mode });
  }, [SECTION, inputs, mode]);
  // Downstream code branches on `framing === "retention"`; alias so a single
  // switch drives every strand of the copy tree.
  const effectiveFraming = mode === "retention" ? "retention" : "onboard";
  const set = (k) => (v) => setInputs((prev) => ({ ...prev, [k]: v }));
  const clear = () => { clearSectionState(SECTION); setInputs({ ...EMPTY_INPUTS }); };

  const out = useMemo(() => {
    const basis = inputs.basis || "monthly";
    const isMonthly = basis === "monthly";

    // Basis-specific inputs — unified downstream into `totalVolume` / `totalUpfront`.
    const V = isMonthly ? num(inputs.monthlyVolume) : 0;
    const T = isMonthly ? num(inputs.tenureMonths) : 0;
    const Umo = isMonthly ? num(inputs.upfrontPerMonth) : 0;
    const Ulump = isMonthly ? 0 : num(inputs.upfrontLumpSum);
    const Vtot = isMonthly ? 0 : num(inputs.totalVolume);

    const c = num(inputs.commissionPct) / 100;
    const f = num(inputs.feesPct) / 100;
    const Rratio = num(inputs.requiredRoi);

    // Unified downstream:
    const totalVolume = isMonthly ? V * T : Vtot;
    const totalUpfront = isMonthly ? Umo * T : Ulump;
    const grossRevenue = totalVolume * f;
    const commissions = grossRevenue * c;
    const totalCost = totalUpfront + commissions;
    const netProfit = grossRevenue - totalCost;

    const expectedRoi = totalCost > 0 ? grossRevenue / totalCost : null;
    const expectedRoiPct = expectedRoi !== null ? expectedRoi * 100 : null;
    // Green light on ROI ≥ Required ROI. Compare at DISPLAY precision (2 dp)
    // so a computed 1.6666… never gets rejected against a Required of 1.67 —
    // if the calculator shows "1.67x vs Required 1.67x", the user rightly
    // expects green. This also swallows any float rounding.
    const VIABILITY_EPS = 0.005;
    const viable = expectedRoi !== null && expectedRoi >= (Rratio - VIABILITY_EPS);

    // ---- Alternative 1: Required VOLUME (in current basis) ---------------
    // Vtotal_needed = Rratio * totalUpfront / (f * (1 - Rratio*c))
    let altTotalVolume = null;
    const denomV = f * (1 - Rratio * c);
    if (Rratio > 0 && totalUpfront > 0 && f > 0) {
      altTotalVolume = denomV > 0 ? (Rratio * totalUpfront) / denomV : Infinity;
    } else if (Rratio === 0 && totalUpfront === 0) {
      altTotalVolume = 0;
    }
    const altVolume = isMonthly && altTotalVolume !== null && isFinite(altTotalVolume) && T > 0
      ? altTotalVolume / T
      : altTotalVolume;
    const currentBasisVol = isMonthly ? V : Vtot;
    const gapVolPct = altVolume !== null && isFinite(altVolume) && currentBasisVol > 0
      ? ((altVolume / currentBasisVol) - 1) * 100 : null;

    // ---- Alternative 2: Max UPFRONT (in current basis) --------------------
    // altUpfrontTotal = totalVolume * f * (1 - Rratio*c) / Rratio
    let altUpfrontTotal = null;
    if (Rratio > 0) {
      const raw = (totalVolume * f * (1 - Rratio * c)) / Rratio;
      altUpfrontTotal = raw > 0 ? raw : 0;
    }
    const altUpfrontMo = isMonthly && altUpfrontTotal !== null && T > 0
      ? altUpfrontTotal / T
      : altUpfrontTotal;

    // ---- Alternative 3: Max COMMISSION % ---------------------------------
    // Formula: c_max = 1/Rratio - U/R (see Alt 2 derivation).
    // When Rratio < 1 or U is tiny, the raw ceiling can exceed 100% — which
    // is a nonsense recommendation because rev-share is physically capped at
    // 100% of gross revenue. We cap the DISPLAY at 100% and reallocate the
    // surplus headroom into an "additional upfront budget" figure so BDs can
    // still see how much extra bargaining room they have (as a flat fee) if
    // they max out commission at 100%.
    let altCommissionPct = null;
    let commissionCapped = false;
    // If we max out commission at 100%, this is the extra flat/upfront budget
    // that would still clear the ROI bar. Total (across the whole deal), so
    // we convert to monthly downstream when we render.
    let extraUpfrontAtMaxCommTotal = null;
    if (totalVolume > 0 && f > 0 && Rratio > 0) {
      const numerator = totalVolume * f - Rratio * totalUpfront;
      const raw = numerator / (Rratio * totalVolume * f);
      const rawPct = raw > 0 ? raw * 100 : 0;
      if (rawPct > 100) {
        altCommissionPct = 100;
        commissionCapped = true;
        // At c = 100%, commissions = totalVolume*f = R (gross revenue).
        // Max supportable upfront under ROI = R/Rratio - R.
        const maxUpfrontAtCap = (totalVolume * f) / Rratio - totalVolume * f;
        extraUpfrontAtMaxCommTotal = Math.max(0, maxUpfrontAtCap - totalUpfront);
      } else {
        altCommissionPct = rawPct;
      }
    }
    const extraUpfrontAtMaxCommMo = isMonthly && extraUpfrontAtMaxCommTotal !== null && T > 0
      ? extraUpfrontAtMaxCommTotal / T
      : extraUpfrontAtMaxCommTotal;

    // ---- Alternative 4: Max ADJUSTED Required ROI -------------------------
    const altRequiredRoi = expectedRoi;

    const requiredKeys = isMonthly ? REQUIRED_KEYS_MONTHLY : REQUIRED_KEYS_TOTAL;
    const allEntered = requiredKeys.every((k) => inputs[k] !== "");

    return {
      basis, isMonthly,
      V, T, Umo, Ulump, Vtot,
      totalUpfront, totalVolume, grossRevenue, commissions, totalCost, netProfit,
      expectedRoi, expectedRoiPct, viable,
      altVolume, altTotalVolume, gapVolPct, altUpfrontMo, altUpfrontTotal, altCommissionPct, altRequiredRoi,
      commissionCapped, extraUpfrontAtMaxCommTotal, extraUpfrontAtMaxCommMo,
      allEntered,
    };
  }, [inputs]);

  const { good, bad, advice: warn, primary, cost } = PALETTE;

  const roiDisplay = out.expectedRoi === null
    ? "—"
    : `${out.expectedRoi.toFixed(2)}x`;

  return (
    <div className="border hairline rounded-md p-6" style={{ background: "var(--surface)" }} data-testid={`${testidPrefix}-calculator-card`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <Calculator size={12} style={{ color: "var(--primary)" }} /> ROI Calculator
          {subject && (<>
            <span className="text-zinc-600">·</span>
            <span>Financial viability for {subject}</span>
          </>)}
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid={`${testidPrefix}-mode-select`}
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            title="Swap copy between winning a new creator (Acquisition) and keeping/renewing one (Retention). Math is identical."
            className="mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border hairline bg-transparent text-zinc-300 hover:text-white focus:outline-none focus:border-cyan-400 transition-colors duration-150"
            style={{ background: "var(--surface-2, #121214)" }}
          >
            <option value="acquisition" style={{ background: "#121214", color: "#F8FAFC" }}>Acquisition Calculator</option>
            <option value="retention"   style={{ background: "#121214", color: "#F8FAFC" }}>Retention Calculator</option>
          </select>
          <button
            data-testid={`${testidPrefix}-clear-btn`}
            onClick={clear}
            className="text-[11px] text-zinc-400 hover:text-cyan-400 flex items-center gap-1"
          ><RotateCcw size={11} /> Clear inputs</button>
        </div>
      </div>

      {/* Volume basis segmented toggle */}
      <div className="mb-4">
        <div className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Volume Basis</div>
        <div className="inline-flex rounded-md border hairline overflow-hidden" data-testid={`${testidPrefix}-basis-toggle`}>
          {[
            { k: "monthly", label: "Monthly Basis" },
            { k: "total", label: "Total Basis" },
          ].map((opt) => (
            <button
              key={opt.k}
              data-testid={`${testidPrefix}-basis-${opt.k}`}
              onClick={() => set("basis")(opt.k)}
              className={`px-4 py-1.5 mono text-xs uppercase tracking-wider transition-colors duration-150 ${
                out.basis === opt.k ? "text-black font-medium" : "text-zinc-400 hover:text-white"
              }`}
              style={out.basis === opt.k ? { background: "var(--primary)" } : {}}
            >{opt.label}</button>
          ))}
        </div>
        <div className="mono text-[10px] text-zinc-600 mt-1.5">
          {out.isMonthly
            ? "Enter a per-month volume and a tenure — the calculator will project the total deal size."
            : "Enter the aggregate volume the KOL has committed to. Tenure is not required."}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {out.isMonthly ? (
          <>
            <RoiField label="Estimated Monthly Volume" prefix="$" value={inputs.monthlyVolume} onChange={set("monthlyVolume")}
              step={10000} testid={`${testidPrefix}-input-monthly-volume`} placeholder="e.g. 1,000,000"
              hint="Trading volume this KOL drives per month" />
            <RoiField label="Tenure" suffix="months" value={inputs.tenureMonths} onChange={set("tenureMonths")}
              step={1} testid={`${testidPrefix}-input-tenure`} placeholder="e.g. 6"
              hint="Length of the promotional deal" />
            <RoiField label="Upfront Cost / month" prefix="$" value={inputs.upfrontPerMonth} onChange={set("upfrontPerMonth")}
              step={500} testid={`${testidPrefix}-input-upfront-per-month`} placeholder="e.g. 1,000"
              hint="Fixed monthly payment to the KOL" />
          </>
        ) : (
          <>
            <RoiField label="Estimated Total Volume" prefix="$" value={inputs.totalVolume} onChange={set("totalVolume")}
              step={100000} testid={`${testidPrefix}-input-total-volume`} placeholder="e.g. 30,000,000"
              hint="Aggregate volume the KOL has committed to deliver" />
            <RoiField label="Total Upfront Cost" prefix="$" value={inputs.upfrontLumpSum} onChange={set("upfrontLumpSum")}
              step={1000} testid={`${testidPrefix}-input-upfront-lump-sum`} placeholder="e.g. 12,000"
              hint="One-time upfront paid to the KOL for this deal" />
          </>
        )}
        <RoiField label="Commission %" suffix="%" value={inputs.commissionPct} onChange={set("commissionPct")}
          step={1} testid={`${testidPrefix}-input-commission-pct`} placeholder="e.g. 20"
          hint="% of fees paid to the KOL as rev-share" />
        <RoiField label="Fees %" suffix="%" value={inputs.feesPct} onChange={set("feesPct")}
          step={0.01} testid={`${testidPrefix}-input-fees-pct`} placeholder="e.g. 0.1"
          hint="Exchange fee applied on traded volume" />
        <RoiField label="Required ROI" suffix="x" value={inputs.requiredRoi} onChange={set("requiredRoi")}
          step={0.1} testid={`${testidPrefix}-input-required-roi`} placeholder="e.g. 1.5"
          hint="Minimum ROI multiplier needed (1.0x = break-even)" />
      </div>

      {!out.allEntered && (
        <div className="text-xs text-zinc-500 flex items-center gap-2 mb-6 border hairline rounded-md px-3 py-2" style={{ background: "var(--surface-2)" }} data-testid={`${testidPrefix}-empty-hint`}>
          <Info size={12} /> Fill in every field above to evaluate the onboarding decision.
        </div>
      )}

      {out.allEntered && (
        <>
          {/* Computation strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-3">
            <OutputBox label="Total Volume" value={fmtUsd(out.totalVolume)} tone={primary}
              testid={`${testidPrefix}-out-total-volume`}
              sub={out.isMonthly ? `${fmtNumber(out.V)} × ${out.T} mo` : "Aggregate volume entered"} />
            <OutputBox label="Gross Revenue" value={fmtUsd(out.grossRevenue)} tone={primary}
              testid={`${testidPrefix}-out-revenue`}
              sub={`Volume × ${inputs.feesPct}% fees`} />
            <OutputBox label="Total Upfront" value={fmtUsd(out.totalUpfront)} tone={cost}
              testid={`${testidPrefix}-out-total-upfront`}
              sub={out.isMonthly ? `${fmtUsd(out.Umo)} / mo × ${out.T} mo` : "One-time lump sum"} />
            <OutputBox label="Total Commissions" value={fmtUsd(out.commissions)} tone={cost}
              testid={`${testidPrefix}-out-commissions`}
              sub={`Revenue × ${inputs.commissionPct}%`} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <OutputBox label="Total Cost" value={fmtUsd(out.totalCost)} tone={cost}
              testid={`${testidPrefix}-out-total-cost`}
              sub="Upfront + Commissions" />
            <OutputBox
              label={out.netProfit >= 0 ? "Net Profit" : "Net Loss"}
              value={fmtUsd(out.netProfit)}
              tone={out.netProfit >= 0 ? good : bad}
              testid={`${testidPrefix}-out-net-profit`}
              sub="Gross Revenue − Total Cost" />
            <OutputBox label="Expected ROI" value={roiDisplay}
              tone={out.viable ? good : bad}
              testid={`${testidPrefix}-out-roi`}
              sub={`Required ${num(inputs.requiredRoi).toFixed(2)}x`} />
          </div>

          {/* Scenario A — Financially Justified (viable) */}
          {out.viable && (
            <div data-testid={`${testidPrefix}-scenario-a`} className="border rounded-md p-5"
              style={{ background: "rgba(57,255,20,0.06)", borderColor: "rgba(57,255,20,0.45)" }}>
              <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: good }}>
                <CheckCircle2 size={14} /> Deal Recommendations
              </div>
              <div className="text-sm text-zinc-200 leading-relaxed mb-4">
                {effectiveFraming === "retention" ? (
                  <>
                    <strong>Retain them — the numbers stack up.</strong> If you keep this KOL on at these terms, you'll earn back{" "}
                    <span className="mono font-semibold" style={{ color: good }}>{out.expectedRoi.toFixed(2)}x</span>{" "}
                    of what you spend, above your{" "}
                    <span className="mono">{num(inputs.requiredRoi).toFixed(2)}x</span> retention bar.{" "}
                    {out.isMonthly ? (
                      <>Over the next <span className="mono">{out.T}</span> months they'll bring in{" "}</>
                    ) : (
                      <>At the volume they've committed to, they'll bring in{" "}</>
                    )}
                    <span className="mono">{fmtUsd(out.grossRevenue)}</span> in fees against{" "}
                    <span className="mono">{fmtUsd(out.totalCost)}</span> in total spend ({fmtUsd(out.totalUpfront)} upfront + {fmtUsd(out.commissions)} in rev-share).
                    {" "}Here's how much room you have to sweeten the deal if they push for better terms:
                  </>
                ) : (
                  <>
                    <strong>Green light — go ahead.</strong> You'll earn back{" "}
                    <span className="mono font-semibold" style={{ color: good }}>{out.expectedRoi.toFixed(2)}x</span>{" "}
                    of what you spend, which is above your{" "}
                    <span className="mono">{num(inputs.requiredRoi).toFixed(2)}x</span> bar.{" "}
                    {out.isMonthly ? (
                      <>Over <span className="mono">{out.T}</span> months this KOL is projected to bring in{" "}</>
                    ) : (
                      <>At full delivery this KOL is projected to bring in{" "}</>
                    )}
                    <span className="mono">{fmtUsd(out.grossRevenue)}</span> in fees against a total spend of{" "}
                    <span className="mono">{fmtUsd(out.totalCost)}</span> ({fmtUsd(out.totalUpfront)} upfront + {fmtUsd(out.commissions)} in rev-share).
                    {" "}Here's your negotiation headroom before this deal stops making sense:
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* A1 — Headroom on Upfront */}
                <AlternativeCard
                  testid={`${testidPrefix}-a-upfront-headroom`}
                  icon={Wallet}
                  tone={good}
                  title="1 · Upfront headroom"
                  value={
                    (out.isMonthly ? out.altUpfrontMo : out.altUpfrontTotal) === null
                      ? "—"
                      : out.isMonthly
                        ? `${fmtUsd(out.altUpfrontMo)} / mo`
                        : `${fmtUsd(out.altUpfrontTotal)} total`
                  }
                  blurb={(() => {
                    const cur = out.isMonthly ? out.Umo : out.Ulump;
                    const ceil = out.isMonthly ? out.altUpfrontMo : out.altUpfrontTotal;
                    if (ceil === null) return `Enter every field to compute the ceiling.`;
                    if (ceil <= cur) return `You're already paying at the ceiling — bumping the upfront a single dollar breaks the ROI bar.`;
                    return out.isMonthly
                      ? `You could raise the upfront from ${fmtUsd(cur)} to ${fmtUsd(ceil)} per month (${fmtUsd(out.altUpfrontTotal)} across the ${out.T}-month deal) and the deal would still clear your ${num(inputs.requiredRoi).toFixed(2)}x bar. That's ${fmtUsd(ceil - cur)}/mo of negotiation room.`
                      : `You could raise the total upfront from ${fmtUsd(cur)} to ${fmtUsd(ceil)} and the deal would still clear your ${num(inputs.requiredRoi).toFixed(2)}x bar. That's ${fmtUsd(ceil - cur)} of negotiation room in aggregate.`;
                  })()}
                />

                {/* A2 — Headroom on Commission (capped at 100% with
                    surplus reallocated to an upfront-fee recommendation) */}
                <AlternativeCard
                  testid={`${testidPrefix}-a-commission-headroom`}
                  icon={Percent}
                  tone={good}
                  title="2 · Commission headroom"
                  value={
                    out.altCommissionPct === null
                      ? "—"
                      : out.commissionCapped
                      ? `100% + ${out.isMonthly ? `${fmtUsd(out.extraUpfrontAtMaxCommMo)}/mo` : fmtUsd(out.extraUpfrontAtMaxCommTotal)}`
                      : `${out.altCommissionPct.toFixed(1)}%`
                  }
                  blurb={(() => {
                    if (out.altCommissionPct === null) return `Enter Volume, Fees and Required ROI to compute the ceiling.`;
                    const isRet = effectiveFraming === "retention";
                    const curPct = num(inputs.commissionPct);
                    // Mode-specific action verbs so the recommendation reads
                    // naturally for acquisition ("offer / concede rev-share")
                    // vs retention ("hold / renegotiate rev-share").
                    if (out.commissionCapped) {
                      // Rev-share ceiling exceeds 100% — cap the recommended
                      // rev-share at 100% and route the leftover value into
                      // an upfront / flat-fee sweetener that keeps ROI green.
                      const extra = out.isMonthly ? out.extraUpfrontAtMaxCommMo : out.extraUpfrontAtMaxCommTotal;
                      const extraLabel = out.isMonthly ? `${fmtUsd(extra)} / month` : `${fmtUsd(extra)} in total`;
                      const totalExtra = fmtUsd(out.extraUpfrontAtMaxCommTotal);
                      if (isRet) {
                        return `Rev-share is physically capped at 100%, so the pure-commission headroom maxes out there. If the KOL threatens to churn, you can safely offer a full 100% rev-share AND sweeten the renewal with up to ${extraLabel}${out.isMonthly ? ` (${totalExtra} across the ${out.T}-month renewal)` : ""} in extra flat retainer while still clearing your ${num(inputs.requiredRoi).toFixed(2)}x retention bar.`;
                      }
                      return `Rev-share is physically capped at 100%, so the pure-commission headroom maxes out there. To close, you can concede a full 100% rev-share AND top the offer up with an extra ${extraLabel}${out.isMonthly ? ` (${totalExtra} across the ${out.T}-month deal)` : ""} upfront / flat fee while still clearing your ${num(inputs.requiredRoi).toFixed(2)}x acquisition bar. That combined package is your absolute negotiation ceiling.`;
                    }
                    if (out.altCommissionPct <= curPct) {
                      return isRet
                        ? `You're already at the retention rev-share ceiling — pushing any higher drops the deal below your ${num(inputs.requiredRoi).toFixed(2)}x bar.`
                        : `You're already at the commission ceiling — any more concession and the ROI drops below your bar.`;
                    }
                    return isRet
                      ? `You can hold rev-share at ${curPct.toFixed(1)}% or, if the creator is pushing back on renewal, lift it up to ${out.altCommissionPct.toFixed(1)}% and still clear your ${num(inputs.requiredRoi).toFixed(2)}x retention bar. That's your renewal bargaining room.`
                      : `You can lift the KOL's rev-share from ${curPct.toFixed(1)}% up to ${out.altCommissionPct.toFixed(1)}% and still hit your ROI. Use this as acquisition bargaining room if they push for a bigger share.`;
                  })()}
                />

                {/* A3 — Volume floor */}
                <AlternativeCard
                  testid={`${testidPrefix}-a-volume-floor`}
                  icon={TrendingUp}
                  tone={good}
                  title="3 · Volume floor"
                  value={
                    out.altVolume === null
                      ? "—"
                      : !isFinite(out.altVolume)
                      ? "Any volume works"
                      : out.isMonthly
                        ? `${fmtUsd(out.altVolume)} / mo`
                        : `${fmtUsd(out.altVolume)} total`
                  }
                  blurb={(() => {
                    const curV = out.isMonthly ? out.V : out.Vtot;
                    const floor = out.altVolume;
                    if (floor === null) return `Enter Required ROI and Upfront to compute the floor.`;
                    if (!isFinite(floor)) return `Given your commission share and Required ROI, the deal never fails on volume alone — it stays justified at any volume above zero.`;
                    if (curV > 0 && floor < curV) {
                      const gap = (((curV - floor) / curV) * 100).toFixed(1);
                      return out.isMonthly
                        ? `The deal stays profitable as long as the KOL delivers at least ${fmtUsd(floor)} per month — that's ${gap}% below your ${fmtUsd(curV)}/mo estimate. If they drop below ${fmtUsd(floor)}, the ROI dips under ${num(inputs.requiredRoi).toFixed(2)}x.`
                        : `The deal stays profitable as long as the KOL delivers at least ${fmtUsd(floor)} total — that's ${gap}% below the ${fmtUsd(curV)} they promised. If aggregate delivery drops below ${fmtUsd(floor)}, the ROI dips under ${num(inputs.requiredRoi).toFixed(2)}x.`;
                    }
                    return out.isMonthly
                      ? `You need at least ${fmtUsd(floor)} per month to stay above your ROI bar.`
                      : `You need at least ${fmtUsd(floor)} in aggregate to stay above your ROI bar.`;
                  })()}
                />

                {/* A4 — Net profit */}
                <AlternativeCard
                  testid={`${testidPrefix}-a-net-profit`}
                  icon={Target}
                  tone={good}
                  title="4 · Expected take-home"
                  value={fmtUsd(out.netProfit)}
                  blurb={(() => {
                    if (out.netProfit <= 0) {
                      return `Ratio-wise the deal clears your bar, but the absolute dollar profit is ${fmtUsd(out.netProfit)}. Worth double-checking whether the effort is worth the reward.`;
                    }
                    return out.isMonthly
                      ? `If the KOL delivers on the ${fmtUsd(out.V)}/mo volume promise, this deal nets you ${fmtUsd(out.netProfit)} in pure profit over ${out.T} months (after ${fmtUsd(out.totalUpfront)} upfront + ${fmtUsd(out.commissions)} in commissions).`
                      : `If the KOL delivers the full ${fmtUsd(out.Vtot)} committed volume, this deal nets you ${fmtUsd(out.netProfit)} in pure profit (after ${fmtUsd(out.totalUpfront)} upfront + ${fmtUsd(out.commissions)} in commissions).`;
                  })()}
                />
              </div>
            </div>
          )}

          {/* Scenario B — Not Justified (below the ROI bar) */}
          {!out.viable && (
            <div data-testid={`${testidPrefix}-scenario-b`} className="border rounded-md p-5"
              style={{ background: "rgba(244,63,94,0.06)", borderColor: "rgba(244,63,94,0.45)" }}>
              <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: bad }}>
                <AlertTriangle size={14} /> Deal Recommendations
              </div>
              <div className="text-sm text-zinc-200 leading-relaxed mb-4">
                {effectiveFraming === "retention" ? (
                  <>
                    <strong>Drop or renegotiate — these terms don't earn their keep.</strong> If you retain this KOL at the current terms you'll only earn back{" "}
                    <span className="mono font-semibold" style={{ color: bad }}>
                      {out.expectedRoi !== null ? `${out.expectedRoi.toFixed(2)}x` : "—"}
                    </span>, but you need{" "}
                    <span className="mono">{num(inputs.requiredRoi).toFixed(2)}x</span> for the retention to be worth it.
                    Any <strong>one</strong> of the four moves below would make them worth keeping:
                  </>
                ) : (
                  <>
                    <strong>Red flag — this deal falls short.</strong> At the current terms you'll only earn back{" "}
                    <span className="mono font-semibold" style={{ color: bad }}>
                      {out.expectedRoi !== null ? `${out.expectedRoi.toFixed(2)}x` : "—"}
                    </span>, but you need{" "}
                    <span className="mono">{num(inputs.requiredRoi).toFixed(2)}x</span> to make it worth the spend.
                    Any <strong>one</strong> of the four changes below would flip this deal to green:
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Alternative 1 — Required Volume */}
                <AlternativeCard
                  testid={`${testidPrefix}-alt-monthly-volume`}
                  icon={TrendingUp}
                  tone={warn}
                  title="1 · Push the volume higher"
                  value={
                    out.altVolume === null
                      ? "—"
                      : !isFinite(out.altVolume)
                      ? "Unreachable"
                      : out.isMonthly
                        ? `${fmtUsd(out.altVolume)} / mo`
                        : `${fmtUsd(out.altVolume)} total`
                  }
                  blurb={(() => {
                    const curV = out.isMonthly ? out.V : out.Vtot;
                    const need = out.altVolume;
                    if (need === null) return `Enter Required ROI and Upfront to compute the target volume.`;
                    if (!isFinite(need)) return `At this commission share and Required ROI, no volume will ever justify the deal — commission share × Required ROI is ≥ 1. Lower one of those first.`;
                    if (out.gapVolPct !== null) {
                      const sign = out.gapVolPct >= 0 ? "+" : "";
                      return out.isMonthly
                        ? `The KOL would need to drive ${fmtUsd(need)} per month — that's ${sign}${out.gapVolPct.toFixed(1)}% vs. your current ${fmtUsd(curV)}/mo estimate. Ask them to commit to that traffic in writing.`
                        : `The KOL would need to commit ${fmtUsd(need)} in total volume — that's ${sign}${out.gapVolPct.toFixed(1)}% above the ${fmtUsd(curV)} on the table. Ask them to guarantee it in writing.`;
                    }
                    return out.isMonthly
                      ? `The KOL would need to drive ${fmtUsd(need)} per month to hit your ROI bar.`
                      : `The KOL would need to commit ${fmtUsd(need)} in total volume to hit your ROI bar.`;
                  })()}
                />

                {/* Alternative 2 — Max Viable Upfront */}
                <AlternativeCard
                  testid={`${testidPrefix}-alt-upfront`}
                  icon={Wallet}
                  tone={warn}
                  title="2 · Trim the upfront"
                  value={
                    (out.isMonthly ? out.altUpfrontMo : out.altUpfrontTotal) === null
                      ? "—"
                      : out.isMonthly
                        ? `${fmtUsd(out.altUpfrontMo)} / mo`
                        : `${fmtUsd(out.altUpfrontTotal)} total`
                  }
                  blurb={(() => {
                    const cap = out.isMonthly ? out.altUpfrontMo : out.altUpfrontTotal;
                    if (cap === null) return `Enter Required ROI to compute the max upfront you can pay.`;
                    if (cap === 0) return `The current commissions alone already blow through your Required ROI — you'd need to pay $0 upfront and still renegotiate the deal. Cut commission or raise the volume.`;
                    return out.isMonthly
                      ? `Cap the monthly upfront at ${fmtUsd(cap)} — that's ${fmtUsd(out.altUpfrontTotal)} across the ${out.T}-month deal. Anything above that breaks your ROI target.`
                      : `Cap the total upfront at ${fmtUsd(cap)}. Anything above that lump sum breaks your ROI target.`;
                  })()}
                />

                {/* Alternative 3 — Max Viable Commission */}
                <AlternativeCard
                  testid={`${testidPrefix}-alt-commission`}
                  icon={Percent}
                  tone={warn}
                  title="3 · Renegotiate commission"
                  value={
                    out.altCommissionPct === null
                      ? "—"
                      : `${out.altCommissionPct.toFixed(1)}%`
                  }
                  blurb={
                    out.altCommissionPct === null
                      ? `Enter Volume, Fees and Required ROI to compute the max commission.`
                      : out.altCommissionPct === 0
                      ? `Not even a 0% commission would rescue this — the upfront alone already blows through your ROI target. ${out.isMonthly ? "Trim the monthly upfront" : "Trim the total upfront"} or push volume up first.`
                      : `Bring the KOL's revenue share down to ${out.altCommissionPct.toFixed(1)}% (from the current ${inputs.commissionPct}%). Everything above that eats into your Required ROI.`
                  }
                />

                {/* Alternative 4 — Max Adjusted Required ROI */}
                <AlternativeCard
                  testid={`${testidPrefix}-alt-required-roi`}
                  icon={Target}
                  tone={warn}
                  title="4 · Lower the ROI bar"
                  value={
                    out.altRequiredRoi === null
                      ? "—"
                      : `${out.altRequiredRoi.toFixed(2)}x`
                  }
                  blurb={
                    out.altRequiredRoi === null
                      ? `Fill Volume, Fees, Commission and Upfront first.`
                      : out.altRequiredRoi <= 0
                      ? `This deal loses money at any positive ROI target — you'd need to give away nothing and still not break even. Walk away or rewrite the terms.`
                      : `If leadership can accept ${out.altRequiredRoi.toFixed(2)}x instead of ${num(inputs.requiredRoi).toFixed(2)}x, this deal already qualifies. That's the ceiling of what these numbers can deliver.`
                  }
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RoiCalculator;
